// admin/GroceryManagement.jsx

import React, { useEffect, useMemo, useState } from "react";

import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Tabs,Tab
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import InventoryIcon from "@mui/icons-material/Inventory";
import RefreshIcon from "@mui/icons-material/Refresh";

import { apiRequest } from "../../fetchSetup";
import CustomDialog from "../../CustomDialog";
import GroceryOrdersTab from "./GroceryOrdersPage";

const PRIMARY = "#006D77";
const SECONDARY = "#00A8B5";
const LIGHT = "#E0F7FA";
const BORDER = "#B2DFDB";

const API_BASE =
  `${process.env.REACT_APP_API_BASE_URL}grocery`;

const CATEGORIES = [
  "Fruits & Vegetables",
  "Dairy & Eggs",
  "Staples",
  "Snacks",
  "Beverages",
  "Personal Care",
  "Household",
  "Baby Care",
  "Bakery",
  "Frozen Foods",
  "Others",
];

const EMPTY_GROCERY = {
  name: "",
  examiner_salt: "",
  drug_type: "",
  manufacturer: "",

  price: 0,
  base_price: 0,
  revenue_percentage: 0,
  discounted_price: 0,

  discount_percentage: 0,
  stock: 0,
  description: "",
  category: "",

  selling_unit: "piece",

  image_url: "",
  prescription_required: false,
  is_active: true,

  storage: "",
  reorder: 0,
  max_qty: 0,

  hsn_code: "",
  cgst: 0,
  sgst: 0,
  igst: 0,

  pack_size: "",
  schedule: "",
  batch_number: "",

  manufacture_date: "",
  expiry_date: "",

  selected_distributors: [],

  brand: "",
  subcategory: "",
};


const authHeaders = () => ({
  Authorization:
    `Bearer ${sessionStorage.getItem("token")}`,
});


function GroceryManagement() {
  const [groceries, setGroceries] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const [form, setForm] =
    useState(EMPTY_GROCERY);

  const [errors, setErrors] = useState({});

  const [selectedImage, setSelectedImage] =
    useState(null);

  const [viewProduct, setViewProduct] =
    useState(null);
  const [stockDialog, setStockDialog] =
    useState({
      open: false,
      product: null,
      stock: 0,
    });

  const [confirmDialog, setConfirmDialog] =
    useState({
      open: false,
      product: null,
      action: "",
    });

  const [customDialog, setCustomDialog] =
    useState({
      open: false,
      title: "",
      message: "",
      type: "info",
    });
  const [tab, setTab] = useState(0);


  const showDialog = (
    message,
    title = "Grocery",
    type = "info"
  ) => {
    setCustomDialog({
      open: true,
      title,
      message,
      type,
    });
  };


  const loadGroceries = async () => {
    setLoading(true);

    try {
      const params = new URLSearchParams();

      if (search.trim()) {
        params.set(
          "search",
          search.trim()
        );
      }

      if (category) {
        params.set(
          "category",
          category
        );
      }

      if (status) {
        params.set(
          "is_active",
          status
        );
      }

      const response = await apiRequest(
        `${API_BASE}/get-grocery?${params.toString()}`,
        {
          method: "GET",
          headers: authHeaders(),
        }
      );

      const result =
        await response.json();

      if (!response.ok) {
        throw new Error(
          result.error ||
          "Failed to load groceries."
        );
      }

      setGroceries(
        result.data || []
      );

    } catch (error) { 
      showDialog(
        error.message ||
        "Failed to load groceries.",
        "Error",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    loadGroceries();
  }, [category, status]);


  const filteredGroceries =
    useMemo(
      () => groceries,
      [groceries]
    );


  const updateField = (
    field,
    value
  ) => {
    setForm(prev => ({
      ...prev,
      [field]: value,
    }));

    setErrors(prev => ({
      ...prev,
      [field]: "",
    }));

    if (
      field === "price" ||
      field === "discounted_price"
    ) {
      const price =
        field === "price"
          ? Number(value)
          : Number(form.price);

      const selling =
        field === "discounted_price"
          ? Number(value)
          : Number(form.discounted_price);

      if (
        price > 0 &&
        selling >= 0 &&
        selling <= price
      ) {
        setForm(prev => ({
          ...prev,
          [field]: value,
          discount_percentage:
            Number(
              (
                ((price - selling) /
                  price) *
                100
              ).toFixed(2)
            ),
        }));
      }
    }
  };


  const validateForm = () => {
    const nextErrors = {};

    if (!form.name.trim()) {
      nextErrors.name =
        "Product name is required.";
    }

    if (!form.category) {
      nextErrors.category =
        "Category is required.";
    }

    const price =
      Number(form.price);

    const discounted =
      Number(form.discounted_price);

    const base =
      Number(form.base_price);

    const stock =
      Number(form.stock);

    if (price < 0) {
      nextErrors.price =
        "Price cannot be negative.";
    }

    if (base < 0) {
      nextErrors.base_price =
        "Base price cannot be negative.";
    }

    if (discounted < 0) {
      nextErrors.discounted_price =
        "Selling price cannot be negative.";
    }

    if (
      discounted > price
    ) {
      nextErrors.discounted_price =
        "Selling price cannot exceed MRP.";
    }

    if (stock < 0) {
      nextErrors.stock =
        "Stock cannot be negative.";
    }

    if (
      Number(form.revenue_percentage) <
      0
    ) {
      nextErrors.revenue_percentage =
        "Revenue percentage cannot be negative.";
    }

    setErrors(nextErrors);

    return (
      Object.keys(nextErrors)
        .length === 0
    );
  };


  const openAdd = () => {
    setEditing(null);
    setForm({
      ...EMPTY_GROCERY,
    });
    setSelectedImage(null);
    setErrors({});
    setDialogOpen(true);
  };


  const openEdit = product => {
    setEditing(product);

    setForm({
      ...EMPTY_GROCERY,
      ...product,
      brand: product.brand || "",
      subcategory:
        product.subcategory || "",
    });

    setSelectedImage(null);
    setErrors({});
    setDialogOpen(true);
  };


  const uploadImage = async (
    groceryId,
    file
  ) => {
    if (!file) return null;

    const body = new FormData();

    body.append(
      "image",
      file
    );

    const response =
      await apiRequest(
        `${API_BASE}/${groceryId}/image`,
        {
          method: "POST",
          headers: authHeaders(),
          body,
        }
      );

    const result =
      await response.json();

    if (!response.ok) {
      throw new Error(
        result.error ||
        "Image upload failed."
      );
    }

    return result.image_url;
  };


  const saveGrocery = async () => {
    if (!validateForm()) {
      showDialog(
        "Please correct the highlighted fields.",
        "Validation",
        "warning"
      );
      return;
    }

    try {
      setLoading(true);

      const payload = {
        ...form,

        price:
          Number(form.price) || 0,

        base_price:
          Number(form.base_price) || 0,

        revenue_percentage:
          Number(
            form.revenue_percentage
          ) || 0,

        discounted_price:
          Number(
            form.discounted_price
          ) || 0,

        discount_percentage:
          Number(
            form.discount_percentage
          ) || 0,

        stock:
          Number(form.stock) || 0,

        reorder:
          Number(form.reorder) || 0,

        max_qty:
          Number(form.max_qty) || 0,

        cgst:
          Number(form.cgst) || 0,

        sgst:
          Number(form.sgst) || 0,

        igst:
          Number(form.igst) || 0,

        service_type: "grocery",
      };

      delete payload.grocery_id;
      delete payload.product_id;
      delete payload.id;

      const url = editing
        ? `${API_BASE}/${editing.grocery_id}`
        : `${API_BASE}/create-grocery`;

      const response =
        await apiRequest(
          url,
          {
            method:
              editing
                ? "PUT"
                : "POST",

            headers: {
              ...authHeaders(),
              "Content-Type":
                "application/json",
            },

            body:
              JSON.stringify(payload),
          }
        );

      const result =
        await response.json();

      if (!response.ok) {
        throw new Error(
          result.error ||
          "Failed to save grocery."
        );
      }

      const grocery =
        result.grocery;

      if (
        selectedImage &&
        grocery?.grocery_id
      ) {
        await uploadImage(
          grocery.grocery_id,
          selectedImage
        );
      }

      setDialogOpen(false);

      await loadGroceries();

      showDialog(
        editing
          ? "Grocery product updated successfully."
          : "Grocery product created successfully.",
        "Success",
        "success"
      );

    } catch (error) {
      showDialog(
        error.message ||
        "Failed to save grocery.",
        "Error",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };


  const changeStatus = async (
    product,
    isActive
  ) => {
    try {
      const response =
        await apiRequest(
          `${API_BASE}/${product.grocery_id}/status`,
          {
            method: "PATCH",
            headers: {
              ...authHeaders(),
              "Content-Type":
                "application/json",
            },
            body:
              JSON.stringify({
                is_active: isActive,
              }),
          }
        );

      const result =
        await response.json();

      if (!response.ok) {
        throw new Error(
          result.error ||
          "Failed to update status."
        );
      }

      await loadGroceries();

    } catch (error) {
      showDialog(
        error.message ||
        "Failed to update status.",
        "Error",
        "error"
      );
    }
  };


  const saveStock = async () => {
    const product =
      stockDialog.product;

    const stock =
      Number(stockDialog.stock);

    if (
      !Number.isInteger(stock) ||
      stock < 0
    ) {
      showDialog(
        "Stock must be a non-negative integer.",
        "Validation",
        "warning"
      );
      return;
    }

    try {
      const response =
        await apiRequest(
          `${API_BASE}/${product.grocery_id}/stock`,
          {
            method: "PATCH",
            headers: {
              ...authHeaders(),
              "Content-Type":
                "application/json",
            },
            body:
              JSON.stringify({
                stock,
              }),
          }
        );

      const result =
        await response.json();

      if (!response.ok) {
        throw new Error(
          result.error ||
          "Failed to update stock."
        );
      }

      setStockDialog({
        open: false,
        product: null,
        stock: 0,
      });

      await loadGroceries();

    } catch (error) {
      showDialog(
        error.message ||
        "Failed to update stock.",
        "Error",
        "error"
      );
    }
  };


  const handleConfirm = async () => {
    const product =
      confirmDialog.product;

    if (
      confirmDialog.action ===
      "deactivate"
    ) {
      await changeStatus(
        product,
        false
      );
    }

    if (
      confirmDialog.action ===
      "activate"
    ) {
      await changeStatus(
        product,
        true
      );
    }

    setConfirmDialog({
      open: false,
      product: null,
      action: "",
    });
  };


  return (
    <Box
      sx={{
        minHeight: "100vh",
        ml: { xs: 0, md: "300px" },
        p: {
          xs: 1.5,
          sm: 2,
          md: 3,
        },
        background:
          "#F7FAFA",
        overflowX: "hidden",
        fontFamily:
          "DM Sans, sans-serif",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          justifyContent:
            "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontFamily:
                "Sora, sans-serif",
              fontWeight: 800,
              color: PRIMARY,
              fontSize: {
                xs: 22,
                md: 28,
              },
            }}
          >
            Grocery Management
          </Typography>

          <Typography
            color="text.secondary"
          >
            Manage grocery products,
            pricing and stock.
          </Typography>
        </Box>

        <Stack
          direction="row"
          spacing={1}
        >
          <IconButton
            onClick={
              loadGroceries
            }
            sx={{
              border:
                `1px solid ${BORDER}`,
            }}
          >
            <RefreshIcon />
          </IconButton>

          <Button
            variant="contained"
            startIcon={
              <AddIcon />
            }
            onClick={openAdd}
            sx={{
              bgcolor: PRIMARY,
              "&:hover": {
                bgcolor:
                  "#00545C",
              },
            }}
          >
            Add Grocery
          </Button>
        </Stack>
      </Box>
<Tabs
  value={tab}
  onChange={(e, v) => setTab(v)}
  sx={{
    mb: 2,
    "& .MuiTabs-indicator": { bgcolor: PRIMARY },
    "& .Mui-selected": { color: `${PRIMARY} !important` },
  }}
>
  <Tab label="Products" sx={{ textTransform: "none", fontWeight: 700 }} />
  <Tab label="Orders" sx={{ textTransform: "none", fontWeight: 700 }} />
</Tabs>
{tab === 0 && (<>
      <Card
        sx={{
          border:
            `1px solid ${BORDER}`,
          borderRadius: 3,
          mb: 2,
          boxShadow: "none",
        }}
      >
        <CardContent>
          <Grid
            container
            spacing={2}
          >
            <Grid
              item
              xs={12}
              md={5}
            >
              <TextField
                fullWidth
                value={search}
                onChange={e =>
                  setSearch(
                    e.target.value
                  )
                }
                onKeyDown={e => {
                  if (
                    e.key ===
                    "Enter"
                  ) {
                    loadGroceries();
                  }
                }}
                placeholder="Search name, brand or product ID"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid
              item
              xs={12}
              sm={6}
              md={3.5}
            >
              <FormControl
                fullWidth
              >
                <InputLabel>
                  Category
                </InputLabel>

                <Select
                  value={category}
                  label="Category"
                  style={{width:204}}
                  onChange={e =>
                    setCategory(
                      e.target.value
                    )
                  }
                >
                  <MenuItem value="">
                    All Categories
                  </MenuItem>

                  {CATEGORIES.map(
                    item => (
                      <MenuItem
                        key={item}
                        value={item}
                      >
                        {item}
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>
            </Grid>

            <Grid
              item
              xs={12}
              sm={6}
              md={3.5}
            >
              <FormControl
                fullWidth
              >
                <InputLabel>
                  Status
                </InputLabel>

                <Select
                  value={status}
                  label="Status"
                  style={{width:204}}
                  onChange={e =>
                    setStatus(
                      e.target.value
                    )
                  }
                >
                  <MenuItem value="">
                    All
                  </MenuItem>
                  <MenuItem value="true">
                    Active
                  </MenuItem>
                  <MenuItem value="false">
                    Inactive
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Box sx={{ mt: 1 }}>
            <Button
              onClick={
                loadGroceries
              }
              sx={{
                color: PRIMARY,
              }}
            >
              Search
            </Button>
          </Box>
        </CardContent>
      </Card>


      <Card
        sx={{
          border:
            `1px solid ${BORDER}`,
          borderRadius: 3,
          boxShadow: "none",
        }}
      >
        <TableContainer
          sx={{
            width: "100%",
            overflowX: "auto",
          }}
        >
          <Table
            sx={{
              minWidth: 900,
            }}
          >
            <TableHead>
              <TableRow
                sx={{
                  bgcolor: LIGHT,
                }}
              >
                <TableCell>
                  Product
                </TableCell>
                <TableCell>
                  Brand
                </TableCell>
                <TableCell>
                  Category
                </TableCell>
                <TableCell>
                  Subcategory
                </TableCell>
                <TableCell>
                  MRP
                </TableCell>
                <TableCell>
                  Selling
                </TableCell>
                <TableCell>
                  Stock
                </TableCell>
                <TableCell>
                  Status
                </TableCell>
                <TableCell
                  align="right"
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={9}
                    align="center"
                  >
                    <CircularProgress
                      size={28}
                      sx={{
                        color:
                          PRIMARY,
                        my: 4,
                      }}
                    />
                  </TableCell>
                </TableRow>
              ) : filteredGroceries.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={9}
                    align="center"
                  >
                    <Typography
                      sx={{
                        py: 5,
                        color:
                          "text.secondary",
                      }}
                    >
                      No grocery
                      products found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredGroceries.map(
                  product => (
                    <TableRow
                      key={
                        product.grocery_id
                      }
                      hover
                    >
                      <TableCell>
                        <Stack
                          direction="row"
                          spacing={1.5}
                          alignItems="center"
                        >
                          <Box
                            component="img"
                            src={
                              product.image_url ||
                              "/placeholder.png"
                            }
                            onError={e => {
                              e.currentTarget.style.display =
                                "none";
                            }}
                            sx={{
                              width: 48,
                              height: 48,
                              objectFit:
                                "contain",
                              borderRadius: 2,
                              border:
                                `1px solid ${BORDER}`,
                              bgcolor:
                                "#fff",
                            }}
                          />

                          <Box>
                            <Typography
                              fontWeight={
                                700
                              }
                            >
                              {
                                product.name
                              }
                            </Typography>

                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {
                                product.grocery_id
                              }
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>

                      <TableCell>
                        {product.brand ||
                          "—"}
                      </TableCell>

                      <TableCell>
                        {product.category}
                      </TableCell>

                      <TableCell>
                        {
                          product.subcategory ||
                          "—"
                        }
                      </TableCell>

                      <TableCell>
                        ₹
                        {Number(
                          product.price ||
                          0
                        ).toFixed(2)}
                      </TableCell>

                      <TableCell>
                        <Typography
                          fontWeight={700}
                          color={PRIMARY}
                        >
                          ₹
                          {Number(
                            product.discounted_price ||
                            0
                          ).toFixed(2)}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Chip
                          icon={
                            <InventoryIcon />
                          }
                          label={
                            product.stock
                          }
                          size="small"
                          sx={{
                            bgcolor:
                              product.stock >
                              0
                                ? LIGHT
                                : "#FFEBEE",
                            color:
                              product.stock >
                              0
                                ? PRIMARY
                                : "#C62828",
                          }}
                        />
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={
                            product.is_active
                              ? "Active"
                              : "Inactive"
                          }
                          size="small"
                          sx={{
                            bgcolor:
                              product.is_active
                                ? "#E8F5E9"
                                : "#F5F5F5",
                            color:
                              product.is_active
                                ? "#2E7D32"
                                : "#666",
                          }}
                        />
                      </TableCell>

                      <TableCell
                        align="right"
                      >
                        <Stack
                          direction="row"
                          spacing={0.5}
                          justifyContent="flex-end"
                        >
                          <IconButton
                            onClick={() =>
                              setViewProduct(
                                product
                              )
                            }
                          >
                            <VisibilityIcon />
                          </IconButton>

                          <IconButton
                            onClick={() =>
                              openEdit(
                                product
                              )
                            }
                          >
                            <EditIcon />
                          </IconButton>

                          <IconButton
                            onClick={() =>
                              setStockDialog({
                                open: true,
                                product,
                                stock:
                                  product.stock ||
                                  0,
                              })
                            }
                          >
                            <InventoryIcon />
                          </IconButton>

                          <IconButton
                            onClick={() =>
                              setConfirmDialog({
                                open: true,
                                product,
                                action:
                                  product.is_active
                                    ? "deactivate"
                                    : "activate",
                              })
                            }
                            sx={{
                              color:
                                product.is_active
                                  ? "#C62828"
                                  : "#2E7D32",
                            }}
                          >
                            {product.is_active ? (
                              <DeleteIcon />
                            ) : (
                              <RefreshIcon />
                            )}
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  )
                )
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

</>)}
{tab === 1 && (
  <GroceryOrdersTab
    apiBase={API_BASE}
    authHeaders={authHeaders}
    showDialog={showDialog}
  />
)}

      {/* ADD / EDIT */}
      <Dialog
        open={dialogOpen}
        onClose={() =>
          setDialogOpen(false)
        }
        fullWidth
        maxWidth="md"
      >
        <DialogTitle
          sx={{
            fontFamily:
              "Sora, sans-serif",
            fontWeight: 800,
            color: PRIMARY,
          }}
        >
          {editing
            ? "Edit Grocery"
            : "Add Grocery"}
        </DialogTitle>

        <DialogContent dividers>
          <GroceryForm
            form={form}
            errors={errors}
            updateField={
              updateField
            }
            setSelectedImage={
              setSelectedImage
            }
          />
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
          }}
        >
          <Button
            onClick={() =>
              setDialogOpen(false)
            }
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={
              saveGrocery
            }
            disabled={loading}
            sx={{
              bgcolor: PRIMARY,
              "&:hover": {
                bgcolor:
                  "#00545C",
              },
            }}
          >
            {loading
              ? "Saving..."
              : editing
                ? "Update"
                : "Create"}
          </Button>
        </DialogActions>
      </Dialog>


      {/* VIEW */}
      <Dialog
        open={!!viewProduct}
        onClose={() =>
          setViewProduct(null)
        }
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Grocery Details
        </DialogTitle>

        <DialogContent dividers>
          {viewProduct && (
            <Stack spacing={1.5}>
              {viewProduct.image_url && (
                <Box
                  component="img"
                  src={
                    viewProduct.image_url
                  }
                  sx={{
                    width: "100%",
                    maxHeight: 260,
                    objectFit:
                      "contain",
                    borderRadius: 2,
                  }}
                />
              )}

              <Typography
                variant="h6"
                fontWeight={700}
              >
                {viewProduct.name}
              </Typography>

              <Typography>
                Brand:{" "}
                {viewProduct.brand ||
                  "—"}
              </Typography>

              <Typography>
                Category:{" "}
                {viewProduct.category}
              </Typography>

              <Typography>
                Subcategory:{" "}
                {viewProduct.subcategory ||
                  "—"}
              </Typography>

              <Typography>
                MRP: ₹
                {Number(
                  viewProduct.price ||
                  0
                ).toFixed(2)}
              </Typography>

              <Typography>
                Selling: ₹
                {Number(
                  viewProduct.discounted_price ||
                  0
                ).toFixed(2)}
              </Typography>

              <Typography>
                Stock:{" "}
                {viewProduct.stock}
              </Typography>

              <Divider />

              <Typography>
                {
                  viewProduct.description
                }
              </Typography>
            </Stack>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() =>
              setViewProduct(null)
            }
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>


      {/* STOCK */}
      <Dialog
        open={stockDialog.open}
        onClose={() =>
          setStockDialog({
            open: false,
            product: null,
            stock: 0,
          })
        }
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          Change Stock
        </DialogTitle>

        <DialogContent>
          <TextField
            fullWidth
            type="number"
            label="Stock"
            value={
              stockDialog.stock
            }
            onChange={e =>
              setStockDialog(
                prev => ({
                  ...prev,
                  stock:
                    e.target.value,
                })
              )
            }
            inputProps={{
              min: 0,
            }}
            sx={{ mt: 1 }}
          />
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() =>
              setStockDialog({
                open: false,
                product: null,
                stock: 0,
              })
            }
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={saveStock}
            sx={{
              bgcolor: PRIMARY,
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>


      {/* CONFIRM */}
      <Dialog
        open={
          confirmDialog.open
        }
        onClose={() =>
          setConfirmDialog({
            open: false,
            product: null,
            action: "",
          })
        }
      >
        <DialogTitle>
          Confirm
        </DialogTitle>

        <DialogContent>
          <Typography>
            Are you sure you want to{" "}
            {confirmDialog.action}{" "}
            <strong>
              {
                confirmDialog
                  .product?.name
              }
            </strong>
            ?
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() =>
              setConfirmDialog({
                open: false,
                product: null,
                action: "",
              })
            }
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={
              handleConfirm
            }
            sx={{
              bgcolor: PRIMARY,
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>


      <CustomDialog
        open={
          customDialog.open
        }
        onClose={() =>
          setCustomDialog(
            prev => ({
              ...prev,
              open: false,
            })
          )
        }
        title={
          customDialog.title
        }
        message={
          customDialog.message
        }
        type={
          customDialog.type
        }
      />
    </Box>
  );
}


function GroceryForm({
  form,
  errors,
  updateField,
  setSelectedImage,
}) {
  const textField = (
    field,
    label,
    options = {}
  ) => (
    <TextField
      fullWidth
      label={label}
      value={
        form[field] ?? ""
      }
      onChange={e =>
        updateField(
          field,
          e.target.value
        )
      }
      error={
        !!errors[field]
      }
      helperText={
        errors[field] || ""
      }
      {...options}
    />
  );


  return (
    <Grid
      container
      spacing={2}
    >
      <Grid item xs={12}>
        {textField(
          "name",
          "Product name *"
        )}
      </Grid>

      <Grid
        item
        xs={12}
        sm={6}
      >
        {textField(
          "brand",
          "Brand"
        )}
      </Grid>

      <Grid
        item
        xs={12}
        sm={6}
      >
        {textField(
          "manufacturer",
          "Manufacturer"
        )}
      </Grid>

      <Grid
        item
        xs={12}
        sm={6}
      >
        <FormControl
          fullWidth
          error={
            !!errors.category
          }
        >
          <InputLabel>
            Category *
          </InputLabel>

          <Select
            value={
              form.category
            }
            label="Category *"
            onChange={e =>
              updateField(
                "category",
                e.target.value
              )
            }
          >
            {CATEGORIES.map(
              item => (
                <MenuItem
                  key={item}
                  value={item}
                >
                  {item}
                </MenuItem>
              )
            )}
          </Select>
        </FormControl>
      </Grid>

      <Grid
        item
        xs={12}
        sm={6}
      >
        {textField(
          "subcategory",
          "Subcategory"
        )}
      </Grid>

      <Grid item xs={12}>
        {textField(
          "description",
          "Description",
          {
            multiline: true,
            minRows: 3,
          }
        )}
      </Grid>

      <Grid
        item
        xs={12}
        sm={4}
      >
        {textField(
          "price",
          "MRP / Price",
          {
            type: "number",
            inputProps: {
              min: 0,
              step: "0.01",
            },
          }
        )}
      </Grid>

      <Grid
        item
        xs={12}
        sm={4}
      >
        {textField(
          "base_price",
          "Base price",
          {
            type: "number",
            inputProps: {
              min: 0,
              step: "0.01",
            },
          }
        )}
      </Grid>

      <Grid
        item
        xs={12}
        sm={4}
      >
        {textField(
          "discounted_price",
          "Selling price",
          {
            type: "number",
            inputProps: {
              min: 0,
              step: "0.01",
            },
          }
        )}
      </Grid>

      <Grid
        item
        xs={12}
        sm={4}
      >
        {textField(
          "discount_percentage",
          "Discount %",
          {
            type: "number",
            inputProps: {
              min: 0,
              step: "0.01",
            },
          }
        )}
      </Grid>

      <Grid
        item
        xs={12}
        sm={4}
      >
        {textField(
          "revenue_percentage",
          "Revenue %",
          {
            type: "number",
            inputProps: {
              min: 0,
              step: "0.01",
            },
          }
        )}
      </Grid>

      <Grid
        item
        xs={12}
        sm={4}
      >
        {textField(
          "stock",
          "Stock",
          {
            type: "number",
            inputProps: {
              min: 0,
            },
          }
        )}
      </Grid>

      <Grid
        item
        xs={12}
        sm={4}
      >
        {textField(
          "selling_unit",
          "Selling unit"
        )}
      </Grid>

      <Grid
        item
        xs={12}
        sm={4}
      >
        {textField(
          "pack_size",
          "Pack size"
        )}
      </Grid>

      <Grid
        item
        xs={12}
        sm={4}
      >
        {textField(
          "storage",
          "Storage"
        )}
      </Grid>

      <Grid
        item
        xs={12}
        sm={4}
      >
        {textField(
          "reorder",
          "Reorder level",
          {
            type: "number",
            inputProps: {
              min: 0,
            },
          }
        )}
      </Grid>

      <Grid
        item
        xs={12}
        sm={4}
      >
        {textField(
          "max_qty",
          "Maximum quantity",
          {
            type: "number",
            inputProps: {
              min: 0,
            },
          }
        )}
      </Grid>

      <Grid
        item
        xs={12}
        sm={4}
      >
        {textField(
          "hsn_code",
          "HSN"
        )}
      </Grid>

      <Grid
        item
        xs={12}
        sm={4}
      >
        {textField(
          "cgst",
          "CGST %",
          {
            type: "number",
            inputProps: {
              min: 0,
            },
          }
        )}
      </Grid>

      <Grid
        item
        xs={12}
        sm={4}
      >
        {textField(
          "sgst",
          "SGST %",
          {
            type: "number",
            inputProps: {
              min: 0,
            },
          }
        )}
      </Grid>

      <Grid
        item
        xs={12}
        sm={4}
      >
        {textField(
          "igst",
          "IGST %",
          {
            type: "number",
            inputProps: {
              min: 0,
            },
          }
        )}
      </Grid>

        <Grid
        item
        xs={12}
        sm={4}
      >
        {textField(
          "image_url",
          "Image URL",
        )}
      </Grid>

    </Grid>
  );
}


export default GroceryManagement;